import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../../services/api';
import usePref from '../../hooks/usePref';
import EventRow, { LugarPicker, ClientPicker, ReferentPicker } from './EventRow';
import TaskRow from './TaskRow';
import ArmadoRow from './ArmadoRow';
import ErrorBoundary from '../../components/ErrorBoundary';

// Fallback de fila: un bug en una fila no debe tumbar la planilla entera.
const rowError = label => (
  <div className="ag-row-error">⚠ No se pudo mostrar {label}. Recargá la página.</div>
);

const EMPTY_COLLAPSED = {};

function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function todayStr() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function prefersReducedMotion() {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
}

// Destello + scroll suave sobre la fila recién creada por un alta inline
// (mismo patrón que scrollToArmado en index.jsx, vía clase temporal en CSS).
function flashNewRow(attr, id) {
  requestAnimationFrame(() => {
    const el = document.querySelector(`[${attr}="${id}"]`);
    if (!el) return;
    const reduce = prefersReducedMotion();
    // Las filas son display:contents (no tienen caja propia): scrollIntoView
    // sobre ellas no hace nada. Se desplaza la primera celda hija, que sí la tiene.
    const box = el.firstElementChild || el;
    box.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    if (reduce) return;
    el.classList.add('is-just-created');
    setTimeout(() => el.classList.remove('is-just-created'), 1000);
  });
}

// ── Filas borrador de alta inline (botón + del header de cada sección) ──
// Restauradas tras el rollback "Restaurar agenda estable" (29bb64e), adaptadas
// a la grilla actual de 8 columnas (celda única de horarios INI/FIN).

function DraftHoursCell({ cellClass, startRef, endRef, startTime, endTime, setStartTime, setEndTime, onEnterEnd, onCancel, disabled }) {
  return (
    <div className={cellClass} style={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
      <input
        ref={startRef}
        className="inline-input inline-mono"
        placeholder="INI"
        title="Inicio (HH:MM)"
        value={startTime}
        disabled={disabled}
        onChange={e => setStartTime(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); endRef.current?.focus(); }
          if (e.key === 'Escape') onCancel();
        }}
      />
      <input
        ref={endRef}
        className="inline-input inline-mono"
        placeholder="FIN"
        title="Fin (HH:MM)"
        value={endTime}
        disabled={disabled}
        onChange={e => setEndTime(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); onEnterEnd(); }
          if (e.key === 'Escape') onCancel();
        }}
      />
    </div>
  );
}

function ArmadoDraftRow({ date, type = 'armado', onAdd, onCancel }) {
  const [phase, setPhase]                   = useState('search');
  const [selectedEvent, setSelectedEvent]   = useState(null);
  const [startTime, setStartTime]           = useState('');
  const [endTime, setEndTime]               = useState('');
  const [searchQuery, setSearchQuery]       = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [dropPos, setDropPos]               = useState(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef         = useRef(null);
  const menuRef          = useRef(null);
  const startRef         = useRef(null);
  const endRef           = useRef(null);
  const searchResultsRef = useRef([]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (searchResults.length > 0 && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    } else {
      setDropPos(null);
    }
    setHighlightIndex(0);
  }, [searchResults]);

  useEffect(() => { if (dropPos) inputRef.current?.focus(); }, [dropPos]);

  useEffect(() => {
    function onMouseDown(e) {
      if (e.target.closest('.armado-draft')) return;
      if (menuRef.current?.contains(e.target)) return;
      onCancel();
    }
    function onScroll() { setSearchResults([]); searchResultsRef.current = []; setDropPos(null); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onCancel]);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); searchResultsRef.current = []; return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/events/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(data);
        searchResultsRef.current = data;
      } catch { setSearchResults([]); searchResultsRef.current = []; }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  function handleSelect(ev) {
    setSelectedEvent(ev);
    setSearchResults([]);
    searchResultsRef.current = [];
    setDropPos(null);
    setPhase('times');
    setTimeout(() => startRef.current?.focus(), 30);
  }

  async function handleSave() {
    if (!selectedEvent) return;
    try {
      const { data } = await api.post('/armados', {
        event_id: selectedEvent.id, armado_date: date, type,
        start_time: startTime || null, end_time: endTime || null,
      });
      onAdd(data);
    } catch (e) { console.error(e); }
  }

  const tailCells = (
    <>
      <div className="armado-cell" style={{ gridColumn: '4 / 5' }} />
      <div className="armado-cell armado-cell-staff" style={{ gridColumn: '5 / 6' }} />
      <div className="armado-cell" style={{ gridColumn: '6 / 7' }} />
      <div className="armado-cell armado-cell-notes" />
      <div className="armado-cell armado-cell-actions" style={{ gridColumn: '8 / 9' }}>
        <button className="kebab-btn" type="button" onClick={onCancel} title="Cancelar" style={{ fontSize: 14 }}>✕</button>
      </div>
    </>
  );

  if (phase === 'times') {
    return (
      <div className="armado-row armado-draft">
        <div className="armado-cell armado-cell-order" />
        <div className="armado-cell armado-cell-evento">
          <div style={{ fontSize: 12, color: 'var(--ag-muted)' }}>
            {type === 'desarmado' ? 'Desarmado' : 'Armado'} — {selectedEvent.name}
          </div>
        </div>
        <DraftHoursCell
          cellClass="armado-cell armado-cell-hours"
          startRef={startRef} endRef={endRef}
          startTime={startTime} endTime={endTime}
          setStartTime={setStartTime} setEndTime={setEndTime}
          onEnterEnd={handleSave} onCancel={onCancel}
        />
        {tailCells}
      </div>
    );
  }

  return (
    <div className="armado-row armado-draft">
      <div className="armado-cell armado-cell-order" />
      <div className="armado-cell armado-cell-evento">
        <input
          ref={inputRef}
          className="inline-input"
          placeholder="Buscar evento para asociar…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') { onCancel(); return; }
            const len = searchResultsRef.current.length;
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIndex(i => Math.min(i + 1, Math.max(0, len - 1))); return; }
            if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlightIndex(i => Math.max(i - 1, 0)); return; }
            if (e.key === 'Enter') {
              if (len > 0) { e.preventDefault(); handleSelect(searchResultsRef.current[highlightIndex]); return; }
              if (!searchQuery.trim()) { e.preventDefault(); onCancel(); }
            }
          }}
        />
        {loading && <div style={{ color: 'var(--ag-dim)', fontSize: 11, marginTop: 2 }}>Buscando…</div>}
        {dropPos && createPortal(
          <div
            ref={menuRef}
            className="import-dropdown"
            onMouseDown={e => e.preventDefault()}
            style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999, margin: 0 }}
          >
            {searchResults.map((r, i) => (
              <div key={r.id} className={`import-option${i === highlightIndex ? ' highlighted' : ''}`} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleSelect(r); }}>
                <div style={{ fontWeight: 700 }}>{r.name}</div>
                <div style={{ color: 'var(--ag-muted)', fontSize: 11 }}>{r.event_date?.slice(0, 10)}{r.place ? ` · ${r.place}` : ''}</div>
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>
      <div className="armado-cell armado-cell-hours" />
      {tailCells}
    </div>
  );
}

function TaskDraftRow({ date, onAdd, onCancel }) {
  const [title, setTitle]         = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime]     = useState('');
  const [saving, setSaving]       = useState(false);
  const titleRef = useRef(null);
  const startRef = useRef(null);
  const endRef   = useRef(null);

  useEffect(() => { titleRef.current?.focus(); }, []);

  useEffect(() => {
    function onMouseDown(e) {
      if (e.target.closest('.task-draft')) return;
      onCancel();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onCancel]);

  async function handleSave() {
    const t = title.trim();
    if (saving) return;
    if (!t) { onCancel(); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/tasks', {
        title: t, task_date: date, status: 'pendiente',
        time: startTime || null, end_time: endTime || null,
      });
      onAdd(data);
    } catch (e) { console.error(e); setSaving(false); }
  }

  return (
    <div className="task-row task-draft">
      <div className="task-cell task-cell-order" />
      <div className="task-cell task-cell-title">
        <input
          ref={titleRef}
          className="inline-input"
          placeholder="Nombre de la tarea…"
          value={title}
          disabled={saving}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (title.trim()) startRef.current?.focus();
              else onCancel();
            }
            if (e.key === 'Escape') onCancel();
          }}
        />
      </div>
      <DraftHoursCell
        cellClass="task-cell task-cell-hours"
        startRef={startRef} endRef={endRef}
        startTime={startTime} endTime={endTime}
        setStartTime={setStartTime} setEndTime={setEndTime}
        onEnterEnd={handleSave} onCancel={onCancel} disabled={saving}
      />
      <div className="task-cell task-cell-placeholder" style={{ gridColumn: '4 / 5' }} />
      <div className="task-cell task-cell-staff" style={{ gridColumn: '5 / 6' }} />
      <div className="task-cell task-cell-placeholder" style={{ gridColumn: '6 / 7' }} />
      <div className="task-cell task-cell-notes" />
      <div className="task-cell task-cell-actions" style={{ gridColumn: '8 / 9' }}>
        <button className="kebab-btn" type="button" onClick={onCancel} title="Cancelar" style={{ fontSize: 14 }}>✕</button>
      </div>
    </div>
  );
}

function EventDraftRow({ date, lugares, clients, salesRefs, onAdd, onCancel }) {
  const [name, setName]           = useState('');
  const [place, setPlace]         = useState('');
  const [client, setClient]       = useState('');
  const [referent, setReferent]   = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime]     = useState('');
  const [saving, setSaving]       = useState(false);
  const nameRef  = useRef(null);
  const startRef = useRef(null);
  const endRef   = useRef(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  useEffect(() => {
    function onMouseDown(e) {
      if (e.target.closest('.event-draft')) return;
      // Los pickers de lugar/cliente/referente abren en portales fuera del
      // draft: interactuar con ellos no debe cancelar el alta.
      if (e.target.closest('.agenda-portal')) return;
      onCancel();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onCancel]);

  async function handleSave() {
    const n = name.trim();
    if (saving) return;
    if (!n) { onCancel(); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/events', {
        name: n, event_date: date, status: 'confirmado',
        place: place || null,
        client: client || null,
        sales_referent: referent || null,
        start_time: startTime || null, end_time: endTime || null,
      });
      onAdd(data);
    } catch (e) { console.error(e); setSaving(false); }
  }

  return (
    <div className="ev-row event-draft">
      <div className="ev-cell ev-cell-order">
        <span className="ev-drag-handle" style={{ opacity: 0.3 }}>≡</span>
      </div>
      <div className="ev-cell ev-cell-evento">
        <div className="ev-evento-header">
          <div className="ev-evento-titles">
            <div className="ev-evento-main">
              <input
                ref={nameRef}
                className="inline-input"
                style={{ fontWeight: 800, fontSize: 14 }}
                placeholder="Nombre del evento…"
                value={name}
                disabled={saving}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (name.trim()) startRef.current?.focus();
                    else onCancel();
                  }
                  if (e.key === 'Escape') onCancel();
                }}
              />
            </div>
            <div className="ev-evento-place">
              {/* Mismos pickers (fuentes de la DB) que EventRow y el drawer —
                  lugar/cliente/referente nunca son texto libre. */}
              <LugarPicker value={place} lugares={lugares} canEdit={!saving} onSave={setPlace} />
            </div>
          </div>
          <span className="status-pill confirmado">confirmado</span>
        </div>
        <div className="ev-evento-bottom">
          <div className="ev-evento-meta">
            <ClientPicker value={client} clients={clients} canEdit={!saving} onSave={setClient} />
            <span className="ev-meta-sep">·</span>
            <ReferentPicker value={referent} salesRefs={salesRefs} canEdit={!saving} onSave={setReferent} />
          </div>
        </div>
      </div>
      <DraftHoursCell
        cellClass="ev-cell ev-cell-hours"
        startRef={startRef} endRef={endRef}
        startTime={startTime} endTime={endTime}
        setStartTime={setStartTime} setEndTime={setEndTime}
        onEnterEnd={handleSave} onCancel={onCancel} disabled={saving}
      />
      <div className="se-cell">
        <div className="se-line" />
        <div className="se-line" />
        <div className="se-line" />
      </div>
      <div className="ev-cell ev-cell-notes" />
      <div className="ev-cell ev-cell-actions">
        <button className="kebab-btn" type="button" onClick={onCancel} title="Cancelar" style={{ fontSize: 14 }}>✕</button>
      </div>
    </div>
  );
}

export default function DayBlock({
  date,
  events,
  tasks,
  armados,
  operations = [],
  isExpanded,
  onToggle,
  canOperate,
  canAdmin,
  canViewEventEquipment,
  onLoad,
  onEditEvent,
  onEditTask,
  onUpdateEvent,
  onUpdateTask,
  onReorderEvents,
  onReorderTasks,
  onReorderArmados,
  onRequestMoveEventDate,
  onCreateEventOperation,
  onAddArmado,
  onAddEvent,
  onAddTask,
  onPdfEventProposal,
  onUpdateArmado,
  onUpdateOperation,
  onRemoveArmado,
  onRemoveOperation,
  onRemoveTask,
  onRemoveEvent,
  onScrollToArmado,
  kbFocus,
  onOpenChecklist,
  personnel, lugares, clients, salesRefs,
  monthConflicts,
  assignmentModel,
  assignmentModelError,
  retryAssignmentModel,
  showColumnHeader = false,
  visibleSections = null,
  forceExpandedSections = false,
  onHorizontalScroll,
}) {
  const [draggedId, setDraggedId]           = useState(null);
  const [draggedTaskId, setDraggedTaskId]   = useState(null);
  const [draggedArmadoId, setDraggedArmadoId] = useState(null);
  const [draggedDesarmadoId, setDraggedDesarmadoId] = useState(null);
  const [draftTask, setDraftTask]           = useState(false);
  const [draftArmado, setDraftArmado]       = useState(false);
  const [draftDesarmado, setDraftDesarmado] = useState(false);
  const [draftEvent, setDraftEvent]         = useState(false);
  // Se incrementan tras cada alta encadenada exitosa: al cambiar la key,
  // React remonta la fila borrador y su estado interno vuelve al inicial
  // (fase de búsqueda, campos vacíos) con foco automático.
  const [taskDraftKey, setTaskDraftKey]           = useState(0);
  const [armadoDraftKey, setArmadoDraftKey]       = useState(0);
  const [desarmadoDraftKey, setDesarmadoDraftKey] = useState(0);
  const [eventDraftKey, setEventDraftKey]         = useState(0);
  // Colapso de secciones (Tareas/Armados/Desarmados/Eventos): preferencia
  // por usuario persistida en el servidor — aplica a todos los días y
  // sobrevive refresh y sesiones.
  const [collapsedSections, setCollapsedSections] = usePref(`agenda.collapsedSections:${date}`, EMPTY_COLLAPSED);
  const toggleSection = key => setCollapsedSections({ ...collapsedSections, [key]: !collapsedSections[key] });
  // Las vistas focalizadas son filtros de la agenda, no otra instancia de sus
  // acordeones. Allí cada sección filtrada siempre debe mostrar sus filas.
  const isSectionCollapsed = key => !forceExpandedSections && !!collapsedSections[key];
  // Arrastrar una fila del tipo correcto sobre el header de una sección colapsada
  // la despliega, para poder soltarla ahí (si no, no hay grilla donde dropear).
  const expandOnDragOver = (key, mime) => e => {
    if (!e.dataTransfer.types.includes(mime)) return;
    if (isSectionCollapsed(key)) {
      e.preventDefault();
      setCollapsedSections({ ...collapsedSections, [key]: false });
    }
  };
  const [pdfDragOver, setPdfDragOver]       = useState(false);
  const [pdfImporting, setPdfImporting]     = useState(false);

  // La tabla armados guarda armados y desarmados juntos (campo type).
  const armadoList    = armados.filter(a => a.type !== 'desarmado');
  const desarmadoList = armados.filter(a => a.type === 'desarmado');
  const operationArmadoList = operations
    .filter(op => op.type !== 'desarmado')
    .map(op => ({ ...op, is_operation: true, row_key: `operation-${op.id}`, armado_date: op.operation_date, notes: op.note }));
  const operationDesarmadoList = operations
    .filter(op => op.type === 'desarmado')
    .map(op => ({ ...op, is_operation: true, row_key: `operation-${op.id}`, armado_date: op.operation_date, notes: op.note }));
  const visibleArmadoList = [...armadoList, ...operationArmadoList];
  const visibleDesarmadoList = [...desarmadoList, ...operationDesarmadoList];
  const tableRef       = useRef(null);
  const dropLineRef    = useRef(null);
  const dropTarget     = useRef(null);
  const taskTableRef   = useRef(null);
  const taskDropLine   = useRef(null);
  const taskDropTarget = useRef(null);
  const armadoTableRef   = useRef(null);
  const armadoDropLine   = useRef(null);
  const armadoDropTarget = useRef(null);
  const desarmadoTableRef   = useRef(null);
  const desarmadoDropLine   = useRef(null);
  const desarmadoDropTarget = useRef(null);

  const scrollRef = useRef(null);
  const columnHeaderRef = useRef(null);
  const syncHeaderScroll = e => {
    if (columnHeaderRef.current) columnHeaderRef.current.scrollLeft = e.currentTarget.scrollLeft;
    onHorizontalScroll?.(e.currentTarget.scrollLeft);
  };

  function findEventRow(el) {
    let cur = el;
    while (cur && cur !== document.body) {
      if (cur.dataset?.eventId) return cur;
      cur = cur.parentElement;
    }
    return null;
  }

  function findTaskRow(el) {
    let cur = el;
    while (cur && cur !== document.body) {
      if (cur.dataset?.taskId) return cur;
      cur = cur.parentElement;
    }
    return null;
  }

  function findArmadoRow(el) {
    let cur = el;
    while (cur && cur !== document.body) {
      if (cur.dataset?.armadoId) return cur;
      cur = cur.parentElement;
    }
    return null;
  }

  function makeDragHandlers(findRow, cellClass, idKey, mimeType, tableRef, dropLineRef, dropTargetRef, draggedId) {
    function onDragOver(e) {
      if (!e.dataTransfer.types.includes(mimeType)) return;
      e.preventDefault();
      const rowEl = findRow(e.target);
      const id = rowEl?.dataset?.[idKey] ? Number(rowEl.dataset[idKey]) : null;
      if (!id || id === draggedId) {
        if (dropLineRef.current) dropLineRef.current.style.display = 'none';
        dropTargetRef.current = null;
        return;
      }
      const firstCell = rowEl.querySelector(`.${cellClass}`);
      if (!firstCell || !tableRef.current) return;
      const cellRect  = firstCell.getBoundingClientRect();
      const tableRect = tableRef.current.getBoundingClientRect();
      const isBefore  = e.clientY < cellRect.top + cellRect.height / 2;
      const lineY     = isBefore ? cellRect.top - tableRect.top : cellRect.bottom - tableRect.top;
      dropTargetRef.current = { id, position: isBefore ? 'before' : 'after' };
      if (dropLineRef.current) {
        dropLineRef.current.style.top     = `${lineY}px`;
        dropLineRef.current.style.display = 'block';
      }
    }
    function onDragLeave(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        if (dropLineRef.current) dropLineRef.current.style.display = 'none';
        dropTargetRef.current = null;
      }
    }
    return { onDragOver, onDragLeave };
  }

  const taskDrag   = makeDragHandlers(findTaskRow,   'task-cell',   'taskId',   'application/x-depot-task',   taskTableRef,   taskDropLine,   taskDropTarget,   draggedTaskId);
  const armadoDrag = makeDragHandlers(findArmadoRow, 'armado-cell', 'armadoId', 'application/x-depot-armado', armadoTableRef, armadoDropLine, armadoDropTarget, draggedArmadoId);
  const desarmadoDrag = makeDragHandlers(findArmadoRow, 'armado-cell', 'armadoId', 'application/x-depot-desarmado', desarmadoTableRef, desarmadoDropLine, desarmadoDropTarget, draggedDesarmadoId);

  function handleDragOver(e) {
    if (!e.dataTransfer.types.includes('application/x-depot-event')) return;
    e.preventDefault();
    const rowEl = findEventRow(e.target);
    const id = rowEl?.dataset?.eventId ? Number(rowEl.dataset.eventId) : null;

    if (!id || id === draggedId) {
      if (dropLineRef.current) dropLineRef.current.style.display = 'none';
      dropTarget.current = null;
      return;
    }

    const firstCell = rowEl.querySelector('.ev-cell');
    if (!firstCell || !tableRef.current) return;

    const cellRect  = firstCell.getBoundingClientRect();
    const tableRect = tableRef.current.getBoundingClientRect();
    const isBefore  = e.clientY < cellRect.top + cellRect.height / 2;
    const lineY     = isBefore ? cellRect.top - tableRect.top : cellRect.bottom - tableRect.top;

    dropTarget.current = { id, position: isBefore ? 'before' : 'after' };

    if (dropLineRef.current) {
      dropLineRef.current.style.top     = `${lineY}px`;
      dropLineRef.current.style.display = 'block';
    }
  }

  function handleDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dropLineRef.current) dropLineRef.current.style.display = 'none';
      dropTarget.current = null;
    }
  }

  async function doReorder(fromId, target) {
    if (!target) return;
    const { id: toId, position } = target;
    const order = events.map(e => e.id);
    const fromIndex = order.indexOf(fromId);
    if (fromIndex === -1) return;
    const newOrder = [...order];
    newOrder.splice(fromIndex, 1);
    const newToIndex = newOrder.indexOf(toId);
    if (newToIndex === -1) return;
    const insertAt = position === 'before' ? newToIndex : newToIndex + 1;
    if (insertAt === fromIndex && order[fromIndex] === fromId) return;
    newOrder.splice(insertAt, 0, fromId);
    onReorderEvents?.(date, newOrder);
    try {
      await api.patch('/events/reorder', { event_date: date, ordered_ids: newOrder });
    } catch (e) { console.error(e); onLoad(); }
  }

  async function doReorderTasks(fromId, target) {
    if (!target) return;
    const { id: toId, position } = target;
    const order = tasks.map(t => t.id);
    const fromIndex = order.indexOf(fromId);
    if (fromIndex === -1) return;
    const newOrder = [...order];
    newOrder.splice(fromIndex, 1);
    const newToIndex = newOrder.indexOf(toId);
    if (newToIndex === -1) return;
    const insertAt = position === 'before' ? newToIndex : newToIndex + 1;
    if (insertAt === fromIndex && order[fromIndex] === fromId) return;
    newOrder.splice(insertAt, 0, fromId);
    onReorderTasks?.(date, newOrder);
    try {
      await api.patch('/tasks/reorder', { task_date: date, ordered_ids: newOrder });
    } catch (e) { console.error(e); onLoad(); }
  }

  async function doReorderArmados(fromId, target) {
    if (!target) return;
    const { id: toId, position } = target;
    const order = armadoList.map(a => a.id);
    const fromIndex = order.indexOf(fromId);
    if (fromIndex === -1) return;
    const newOrder = [...order];
    newOrder.splice(fromIndex, 1);
    const newToIndex = newOrder.indexOf(toId);
    if (newToIndex === -1) return;
    const insertAt = position === 'before' ? newToIndex : newToIndex + 1;
    if (insertAt === fromIndex && order[fromIndex] === fromId) return;
    newOrder.splice(insertAt, 0, fromId);
    onReorderArmados?.(date, newOrder);
    try {
      await api.patch('/armados/reorder', { armado_date: date, ordered_ids: newOrder });
    } catch (e) { console.error(e); onLoad(); }
  }

  async function doReorderDesarmados(fromId, target) {
    if (!target) return;
    const { id: toId, position } = target;
    const order = desarmadoList.map(a => a.id);
    const fromIndex = order.indexOf(fromId);
    if (fromIndex === -1) return;
    const newOrder = [...order];
    newOrder.splice(fromIndex, 1);
    const newToIndex = newOrder.indexOf(toId);
    if (newToIndex === -1) return;
    const insertAt = position === 'before' ? newToIndex : newToIndex + 1;
    if (insertAt === fromIndex && order[fromIndex] === fromId) return;
    newOrder.splice(insertAt, 0, fromId);
    onReorderArmados?.(date, newOrder);
    try {
      await api.patch('/armados/reorder', { armado_date: date, ordered_ids: newOrder });
    } catch (e) { console.error(e); onLoad(); }
  }

  const isToday = date === todayStr();
  const isPast  = date < todayStr();
  const d = parseLocalDate(date);
  const rawLabel = format(d, "EEEE d 'de' MMMM", { locale: es });
  const fullDate = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);

  const enabledSections = new Set(visibleSections || ['eventos', 'tareas', 'armados', 'desarmados']);
  const showEvents = enabledSections.has('eventos');
  const showArmados = enabledSections.has('armados');
  const showDesarmados = enabledSections.has('desarmados');
  const countParts = [];
  if (showEvents && events.length > 0) countParts.push(`${events.length} evento${events.length !== 1 ? 's' : ''}`);
  if (enabledSections.has('tareas') && tasks.length > 0) countParts.push(`${tasks.length} tarea${tasks.length !== 1 ? 's' : ''}`);
  if (showArmados && visibleArmadoList.length > 0) countParts.push(`${visibleArmadoList.length} armado${visibleArmadoList.length !== 1 ? 's' : ''}`);
  if (showDesarmados && visibleDesarmadoList.length > 0) countParts.push(`${visibleDesarmadoList.length} desarmado${visibleDesarmadoList.length !== 1 ? 's' : ''}`);
  const countLabel = countParts.join(' · ');

  const showTasks = enabledSections.has('tareas') && (tasks.length > 0 || canOperate);

  const collapsedClass = !isExpanded ? ' is-collapsed' : '';
  const todayClass     = isToday    ? ' day-block-today' : '';
  const pastClass      = isPast     ? ' day-block-past'  : '';

  return (
    <div
      className={`day-block${collapsedClass}${todayClass}${pastClass}${pdfDragOver ? ' pdf-drop-target' : ''}`}
      data-day={date}
      onDragOver={e => {
        if (!canAdmin) return;
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setPdfDragOver(true);
      }}
      onDragLeave={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPdfDragOver(false);
      }}
      onDrop={async e => {
        e.preventDefault();
        setPdfDragOver(false);
        if (!canAdmin) return;
        const file = Array.from(e.dataTransfer.files).find(f => f.type === 'application/pdf');
        if (!file) return;
        setPdfImporting(true);
        try {
          const formData = new FormData();
          formData.append('pdf', file);
          formData.append('fallback_date', date);
          const { data } = await api.post('/events/import-pdf', formData);
          onPdfEventProposal?.(data, date);
        } catch (err) {
          alert('Error al leer el PDF: ' + (err.response?.data?.error || err.message));
        } finally {
          setPdfImporting(false);
        }
      }}
    >
      <div className="day-sticky">
        <button className="day-strip" type="button" onClick={onToggle}>
          <div className="day-title">
            <strong>{fullDate}</strong>
            {isToday && <span className="day-hoy-tag">HOY</span>}
            {countLabel && <span className="day-title-count">{countLabel}</span>}
          </div>
          <div className="day-right">
            <span className="day-chevron">⌄</span>
          </div>
        </button>
      </div>

      {pdfImporting && (
        <div className="pdf-importing-bar">Leyendo PDF y preparando el formulario…</div>
      )}
      {pdfDragOver && (
        <div className="pdf-drop-overlay">
          <span>Soltar para prellenar un evento</span>
        </div>
      )}

      {showColumnHeader && isExpanded && (
        <div className="day-col-sticky" ref={columnHeaderRef}>
          <div className="column-strip day-col-strip">
            <span>Orden</span>
            <span>Evento</span>
            <span className="col-center">Horarios</span>
            <span className="col-center">Armado</span>
            <span className="col-center">Operación</span>
            <span className="col-center">Desarmado</span>
            <span>Notas</span>
            <span />
          </div>
        </div>
      )}

      <div className="day-content">
        {/* Único contenedor de scroll horizontal — engloba las 3 secciones */}
        <div className="day-scroll" ref={scrollRef} onScroll={syncHeaderScroll}>
          <div className="day-scroll-inner">

            {/* Tareas */}
            {showTasks && (
              <section className={`task-section${tasks.length === 0 ? ' is-empty-mob' : ''}`}>
                <div className="section-title-bar section-collapsible" onClick={() => toggleSection('tareas')} onDragOver={expandOnDragOver('tareas', 'application/x-depot-task')}>
                  <span className="section-title-label">
                    <span className="section-caret">{isSectionCollapsed('tareas') ? '▸' : '▾'}</span>
                    Tareas
                    <span className="section-count">{tasks.length}</span>
                  </span>
                  {canOperate && (
                    <button
                      type="button"
                      className="section-add-btn"
                      onClick={e => { e.stopPropagation(); setCollapsedSections({ ...collapsedSections, tareas: false }); setDraftTask(true); }}
                      title="Nueva tarea"
                      disabled={draftTask}
                    >
                      +
                    </button>
                  )}
                </div>
                {!isSectionCollapsed('tareas') && (
                <div
                  className="task-grid"
                  ref={taskTableRef}
                  onDragOver={taskDrag.onDragOver}
                  onDrop={e => {
                    e.preventDefault();
                    if (taskDropLine.current) taskDropLine.current.style.display = 'none';
                    try {
                      const { id, sourceDate } = JSON.parse(e.dataTransfer.getData('text/plain'));
                      if (sourceDate === date && taskDropTarget.current) doReorderTasks(id, taskDropTarget.current);
                    } catch {}
                    setDraggedTaskId(null);
                    taskDropTarget.current = null;
                  }}
                  onDragLeave={taskDrag.onDragLeave}
                >
                  {tasks.map((t, i) => (
                    <ErrorBoundary key={t.id} resetKey={t.updated_at} fallback={rowError('esta tarea')}>
                    <TaskRow
                      task={t}
                      index={i}
                      canOperate={canOperate}
                      onLoad={onLoad}
                      onEdit={() => onEditTask(t)}
                      onRemoveTask={onRemoveTask}
                      kbFocused={kbFocus?.type === 'task' && kbFocus.id === t.id}
                      isDragging={draggedTaskId === t.id}
                      onDragStart={() => setDraggedTaskId(t.id)}
                      onDragEnd={() => {
                        setDraggedTaskId(null);
                        taskDropTarget.current = null;
                        if (taskDropLine.current) taskDropLine.current.style.display = 'none';
                      }}
                      canDrag={canOperate}
                      onUpdateTask={patch => onUpdateTask?.(t.id, patch)}
                      personnel={personnel}
                      assignmentModel={assignmentModel}
                      assignmentModelError={assignmentModelError}
                      retryAssignmentModel={retryAssignmentModel}
                      conflictKeys={monthConflicts ? (monthConflicts.tasks[t.id] ?? null) : undefined}
                    />
                    </ErrorBoundary>
                  ))}
                  {draftTask && (
                    <TaskDraftRow
                      key={taskDraftKey}
                      date={date}
                      onAdd={data => {
                        onAddTask?.(data);
                        flashNewRow('data-task-id', data.id);
                        setTaskDraftKey(k => k + 1);
                      }}
                      onCancel={() => setDraftTask(false)}
                    />
                  )}
                  <div className="ev-drop-line" ref={taskDropLine} style={{ display: 'none' }} />
                </div>
                )}
              </section>
            )}

            {/* Armados — siempre visible, entre Tareas y Eventos */}
            {showArmados && (<section className={`armado-section${visibleArmadoList.length === 0 ? ' is-empty-mob' : ''}`}>
              <div className="section-title-bar section-collapsible" onClick={() => toggleSection('armados')} onDragOver={expandOnDragOver('armados', 'application/x-depot-armado')}>
                <span className="section-title-label">
                  <span className="section-caret">{isSectionCollapsed('armados') ? '▸' : '▾'}</span>
                  Armados
                  <span className="section-count">{visibleArmadoList.length}</span>
                </span>
                {canAdmin && (
                  <button
                    type="button"
                    className="section-add-btn"
                    onClick={e => { e.stopPropagation(); setCollapsedSections({ ...collapsedSections, armados: false }); setDraftArmado(true); }}
                    title="Nuevo armado"
                    disabled={draftArmado}
                  >
                    +
                  </button>
                )}
              </div>
              {!isSectionCollapsed('armados') && (
                <div
                  className="armado-grid"
                  ref={armadoTableRef}
                  onDragOver={armadoDrag.onDragOver}
                  onDrop={e => {
                    e.preventDefault();
                    if (armadoDropLine.current) armadoDropLine.current.style.display = 'none';
                    try {
                      const { id, sourceDate } = JSON.parse(e.dataTransfer.getData('text/plain'));
                      if (sourceDate === date && armadoDropTarget.current) doReorderArmados(id, armadoDropTarget.current);
                    } catch {}
                    setDraggedArmadoId(null);
                    armadoDropTarget.current = null;
                  }}
                  onDragLeave={armadoDrag.onDragLeave}
                >
                  {visibleArmadoList.map((a, i) => (
                    <ErrorBoundary key={a.row_key || a.id} resetKey={a.updated_at} fallback={rowError('este armado')}>
                    <ArmadoRow
                      armado={a}
                      index={i}
                      canAdmin={canAdmin}
                      onLoad={onLoad}
                      onUpdateArmado={patch => a.is_operation ? onUpdateOperation?.(a.id, patch) : onUpdateArmado?.(a.id, patch)}
                      onRemoveArmado={() => a.is_operation ? onRemoveOperation?.(a.id) : onRemoveArmado?.(a.id)}
                      isDragging={draggedArmadoId === a.id && !a.is_operation}
                      onDragStart={() => setDraggedArmadoId(a.id)}
                      onDragEnd={() => {
                        setDraggedArmadoId(null);
                        armadoDropTarget.current = null;
                        if (armadoDropLine.current) armadoDropLine.current.style.display = 'none';
                      }}
                      canDrag={canAdmin && !a.is_operation}
                      personnel={personnel}
                      assignmentModel={assignmentModel}
                      assignmentModelError={assignmentModelError}
                      retryAssignmentModel={retryAssignmentModel}
                      kbFocused={kbFocus?.type === 'armado' && kbFocus.id === a.id}
                      conflictKeys={monthConflicts ? (a.is_operation ? (monthConflicts.operations?.[a.id] ?? null) : (monthConflicts.armados[a.id] ?? null)) : undefined}
                    />
                    </ErrorBoundary>
                  ))}
                  {draftArmado && (
                    <ArmadoDraftRow
                      key={armadoDraftKey}
                      date={date}
                      onAdd={data => {
                        onAddArmado?.(data);
                        flashNewRow('data-armado-id', data.id);
                        setArmadoDraftKey(k => k + 1);
                      }}
                      onCancel={() => setDraftArmado(false)}
                    />
                  )}
                  <div className="ev-drop-line" ref={armadoDropLine} style={{ display: 'none' }} />
                </div>
              )}
            </section>)}

            {/* Desarmados — misma tabla (type=desarmado), entre Armados y Eventos */}
            {showDesarmados && (<section className={`armado-section desarmado-section${visibleDesarmadoList.length === 0 ? ' is-empty-mob' : ''}`}>
              <div className="section-title-bar section-collapsible" onClick={() => toggleSection('desarmados')} onDragOver={expandOnDragOver('desarmados', 'application/x-depot-desarmado')}>
                <span className="section-title-label">
                  <span className="section-caret">{isSectionCollapsed('desarmados') ? '▸' : '▾'}</span>
                  Desarmados
                  <span className="section-count">{visibleDesarmadoList.length}</span>
                </span>
                {canAdmin && (
                  <button
                    type="button"
                    className="section-add-btn"
                    onClick={e => { e.stopPropagation(); setCollapsedSections({ ...collapsedSections, desarmados: false }); setDraftDesarmado(true); }}
                    title="Nuevo desarmado"
                    disabled={draftDesarmado}
                  >
                    +
                  </button>
                )}
              </div>
              {!isSectionCollapsed('desarmados') && (
                <div
                  className="armado-grid"
                  ref={desarmadoTableRef}
                  onDragOver={desarmadoDrag.onDragOver}
                  onDrop={e => {
                    e.preventDefault();
                    if (desarmadoDropLine.current) desarmadoDropLine.current.style.display = 'none';
                    try {
                      const { id, sourceDate } = JSON.parse(e.dataTransfer.getData('text/plain'));
                      if (sourceDate === date && desarmadoDropTarget.current) doReorderDesarmados(id, desarmadoDropTarget.current);
                    } catch {}
                    setDraggedDesarmadoId(null);
                    desarmadoDropTarget.current = null;
                  }}
                  onDragLeave={desarmadoDrag.onDragLeave}
                >
                  {visibleDesarmadoList.map((a, i) => (
                    <ErrorBoundary key={a.row_key || a.id} resetKey={a.updated_at} fallback={rowError('este desarmado')}>
                    <ArmadoRow
                      armado={a}
                      index={i}
                      canAdmin={canAdmin}
                      onLoad={onLoad}
                      onUpdateArmado={patch => a.is_operation ? onUpdateOperation?.(a.id, patch) : onUpdateArmado?.(a.id, patch)}
                      onRemoveArmado={() => a.is_operation ? onRemoveOperation?.(a.id) : onRemoveArmado?.(a.id)}
                      isDragging={draggedDesarmadoId === a.id && !a.is_operation}
                      onDragStart={() => setDraggedDesarmadoId(a.id)}
                      onDragEnd={() => {
                        setDraggedDesarmadoId(null);
                        desarmadoDropTarget.current = null;
                        if (desarmadoDropLine.current) desarmadoDropLine.current.style.display = 'none';
                      }}
                      canDrag={canAdmin && !a.is_operation}
                      dragMime="application/x-depot-desarmado"
                      personnel={personnel}
                      assignmentModel={assignmentModel}
                      assignmentModelError={assignmentModelError}
                      retryAssignmentModel={retryAssignmentModel}
                      kbFocused={kbFocus?.type === 'armado' && kbFocus.id === a.id}
                      conflictKeys={monthConflicts ? (a.is_operation ? (monthConflicts.operations?.[a.id] ?? null) : (monthConflicts.armados[a.id] ?? null)) : undefined}
                    />
                    </ErrorBoundary>
                  ))}
                  {draftDesarmado && (
                    <ArmadoDraftRow
                      key={desarmadoDraftKey}
                      date={date}
                      type="desarmado"
                      onAdd={data => {
                        onAddArmado?.(data);
                        flashNewRow('data-armado-id', data.id);
                        setDesarmadoDraftKey(k => k + 1);
                      }}
                      onCancel={() => setDraftDesarmado(false)}
                    />
                  )}
                  <div className="ev-drop-line" ref={desarmadoDropLine} style={{ display: 'none' }} />
                </div>
              )}
            </section>)}

            {/* Eventos */}
            {showEvents && (<section className={`event-section${events.length === 0 ? ' is-empty-mob' : ''}`}>
              <div className="section-title-bar section-collapsible" onClick={() => toggleSection('eventos')} onDragOver={expandOnDragOver('eventos', 'application/x-depot-event')}>
                <span className="section-title-label">
                  <span className="section-caret">{isSectionCollapsed('eventos') ? '▸' : '▾'}</span>
                  Eventos
                  <span className="section-count">{events.length}</span>
                </span>
                {canOperate && (
                  <button
                    type="button"
                    className="section-add-btn"
                    onClick={e => { e.stopPropagation(); setCollapsedSections({ ...collapsedSections, eventos: false }); setDraftEvent(true); }}
                    title="Nuevo evento"
                    disabled={draftEvent}
                  >
                    +
                  </button>
                )}
              </div>
              {!isSectionCollapsed('eventos') && (
              <div
                className="ev-table"
                ref={tableRef}
                onDragOver={handleDragOver}
                onDrop={e => {
                  e.preventDefault();
                  if (dropLineRef.current) dropLineRef.current.style.display = 'none';
                  try {
                    const { id, sourceDate } = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (sourceDate === date && dropTarget.current) doReorder(id, dropTarget.current);
                  } catch {}
                  setDraggedId(null);
                  dropTarget.current = null;
                }}
                onDragLeave={handleDragLeave}
              >
                {events.map((ev, i) => (
                  <ErrorBoundary key={ev.id} resetKey={ev.updated_at} fallback={rowError('este evento')}>
                  <EventRow
                    event={ev}
                    index={i}
                    canOperate={canOperate}
                    canAdmin={canAdmin}
                    canViewEventEquipment={canViewEventEquipment}
                    onLoad={onLoad}
                    onEdit={() => onEditEvent(ev)}
                    onRemoveEvent={onRemoveEvent}
                    kbFocused={kbFocus?.type === 'event' && kbFocus.id === ev.id}
                    isDragging={draggedId === ev.id}
                    onOpenChecklist={onOpenChecklist}
                    onDragStart={() => setDraggedId(ev.id)}
                    onDragEnd={() => {
                      setDraggedId(null);
                      dropTarget.current = null;
                      if (dropLineRef.current) dropLineRef.current.style.display = 'none';
                    }}
                    canDrag={canAdmin}
                    onRequestMoveDate={onRequestMoveEventDate}
                    onUpdateEvent={patch => onUpdateEvent?.(ev.id, patch)}
                    onScrollToArmado={onScrollToArmado}
                    onCreateAuxOperation={onCreateEventOperation}
                    personnel={personnel}
                    assignmentModel={assignmentModel}
                    assignmentModelError={assignmentModelError}
                    retryAssignmentModel={retryAssignmentModel}
                    lugares={lugares}
                    clients={clients}
                    salesRefs={salesRefs}
                    conflictKeys={monthConflicts ? (monthConflicts.events[ev.id] ?? null) : undefined}
                  />
                  </ErrorBoundary>
                ))}
                {draftEvent && (
                  <EventDraftRow
                    key={eventDraftKey}
                    date={date}
                    lugares={lugares}
                    clients={clients}
                    salesRefs={salesRefs}
                    onAdd={data => {
                      onAddEvent?.(data);
                      // Si el evento cayó en otro mes (p.ej. día de "arrastre" al
                      // borde del calendario) la vista navega a ese mes — no hay
                      // fila que destacar ni borrador de este día que encadenar.
                      const sameMonth = data.event_date?.slice(0, 7) === date.slice(0, 7);
                      if (!sameMonth) { setDraftEvent(false); return; }
                      flashNewRow('data-event-id', data.id);
                      setEventDraftKey(k => k + 1);
                    }}
                    onCancel={() => setDraftEvent(false)}
                  />
                )}
                <div className="ev-drop-line" ref={dropLineRef} style={{ display: 'none' }} />
              </div>
              )}
            </section>)}

          </div>
        </div>
      </div>
    </div>
  );
}
